import React, { useCallback, useState } from 'react';
import type { ColDef } from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  LopHistoryDateFilter,
  isoToday,
  isoTwoWeeksAgo,
} from '../../components/LopHistoryDateFilter';
import { getAssistantLog } from '../../api/assistantLog';
import type { AssistantLogRow } from '../../types/Monitoring';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

// "יומן עוזר AI" — read-only viewer of dbo.AssistantLog, SysAdmin-only.
// Reuses the LopCrudPage shell to inherit search / export / customization /
// context menu. There's no Edit permission for the `assistantLog` resource
// (DB-side), so LopCrudPage hides Add / Delete and `saveBatch` is never
// actually invoked — the no-op below is the contract requirement.

function feedbackLabel(v: number | null | undefined): string {
  if (v === 1) return '👍';
  if (v === 2) return '👎';
  return '';
}

function fmtDateTime(value: unknown): string {
  return formatDateTimeIL(value, { seconds: true });
}

const columnDefs: ColDef<AssistantLogRow>[] = [
  { field: 'loggedAt', headerName: t('assistantLog.columns.loggedAt'), width: 160,
    valueFormatter: (p) => fmtDateTime(p.value) },
  { field: 'userDisplayName', headerName: t('assistantLog.columns.user'), width: 140 },
  { field: 'route', headerName: t('assistantLog.columns.route'), width: 180 },
  { field: 'question', headerName: t('assistantLog.columns.question'), flex: 1, minWidth: 260,
    tooltipField: 'question' },
  { field: 'answer', headerName: t('assistantLog.columns.answer'), flex: 1, minWidth: 260,
    tooltipField: 'answer' },
  { field: 'retrievedChunks', headerName: t('assistantLog.columns.chunks'), width: 90,
    cellDataType: 'number' },
  { field: 'inputTokens', headerName: t('assistantLog.columns.inputTokens'), width: 90,
    cellDataType: 'number' },
  { field: 'outputTokens', headerName: t('assistantLog.columns.outputTokens'), width: 90,
    cellDataType: 'number' },
  { field: 'feedback', headerName: t('assistantLog.columns.feedback'), width: 90,
    valueFormatter: (p) => feedbackLabel(p.value as number | null | undefined) },
  { field: 'errorMessage', headerName: t('assistantLog.columns.error'), flex: 1, minWidth: 200,
    tooltipField: 'errorMessage' },
];

const newRowDefaults = (): Partial<AssistantLogRow> => ({});

export function AssistantLogPage() {
  const [draftFrom, setDraftFrom] = useState<string>(isoTwoWeeksAgo);
  const [draftTo, setDraftTo] = useState<string>(isoToday);
  // No data is loaded until the first click on "Fetch" (see LopCrudPage.awaitingFetch).
  const [fetched, setFetched] = useState<boolean>(false);
  const [activeFrom, setActiveFrom] = useState<string>(draftFrom);
  const [activeTo, setActiveTo] = useState<string>(draftTo);

  const getRows = useCallback(
    () => getAssistantLog(activeFrom, activeTo),
    [activeFrom, activeTo],
  );

  // No-op: this page is read-only (no Edit permission). LopCrudPage never
  // invokes saveBatch when there are no add/modify/delete states, so this
  // stub exists purely to satisfy the prop contract.
  const saveBatch = useCallback(async () => [], []);

  return (
    <div className="lop-history-page">
      <LopHistoryDateFilter
        fromDate={draftFrom}
        toDate={draftTo}
        onFromChange={setDraftFrom}
        onToChange={setDraftTo}
        onApply={() => {
          setActiveFrom(draftFrom);
          setActiveTo(draftTo);
          setFetched(true);
        }}
      />
      <LopCrudPage<AssistantLogRow>
        awaitingFetch={!fetched}
        resourceCode="assistantLog"
        titleKey="assistantLog.pageTitle"
        idField="assistantLogId"
        getRows={getRows}
        saveBatch={saveBatch}
        columnDefs={columnDefs}
        newRowDefaults={newRowDefaults}
      />
    </div>
  );
}
